export type LiveLocationStatus =
  | "idle"
  | "starting"
  | "active"
  | "denied"
  | "unavailable"
  | "timeout";

export interface LiveLocationFix {
  lat: number;
  lon: number;
  accuracy?: number;
}

export interface LiveLocationCallbacks {
  onUpdate(fix: LiveLocationFix, shouldRecenter: boolean): void;
  onStatus(status: LiveLocationStatus): void;
}

export interface LiveLocationController {
  start(auto?: boolean): void;
  stop(): void;
  recenter(): void;
  latest(): LiveLocationFix | null;
  status(): LiveLocationStatus;
}

export function createLiveLocationController(
  geolocation: Geolocation | undefined,
  callbacks: LiveLocationCallbacks,
): LiveLocationController {
  let watchId: number | null = null;
  let latestFix: LiveLocationFix | null = null;
  let currentStatus: LiveLocationStatus = "idle";
  let autoRecenterPending = false;
  let denied = false;

  const setStatus = (status: LiveLocationStatus) => {
    currentStatus = status;
    callbacks.onStatus(status);
  };

  return {
    start(auto = false) {
      if (!geolocation) {
        setStatus("unavailable");
        return;
      }
      if (watchId !== null || (auto && denied)) return;
      autoRecenterPending = auto && !latestFix;
      setStatus("starting");
      watchId = geolocation.watchPosition(
        (position) => {
          denied = false;
          latestFix = {
            lat: position.coords.latitude,
            lon: position.coords.longitude,
            accuracy: Number.isFinite(position.coords.accuracy)
              ? position.coords.accuracy
              : undefined,
          };
          setStatus("active");
          callbacks.onUpdate(latestFix, autoRecenterPending);
          autoRecenterPending = false;
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            if (watchId !== null) {
              geolocation.clearWatch(watchId);
            }
            watchId = null;
            autoRecenterPending = false;
            denied = true;
            setStatus("denied");
            return;
          }
          if (error.code === error.TIMEOUT) {
            setStatus("timeout");
            return;
          }
          setStatus("unavailable");
        },
        { enableHighAccuracy: true, maximumAge: 5000, timeout: 12000 },
      );
    },
    stop() {
      if (watchId !== null && geolocation) {
        geolocation.clearWatch(watchId);
      }
      watchId = null;
      autoRecenterPending = false;
      if (currentStatus === "starting" || currentStatus === "active") {
        setStatus("idle");
      }
    },
    recenter() {
      if (latestFix) {
        callbacks.onUpdate(latestFix, true);
        return;
      }
      this.start(false);
    },
    latest() {
      return latestFix;
    },
    status() {
      return currentStatus;
    },
  };
}

export function isMobileViewport(width = window.innerWidth): boolean {
  return width <= 760;
}
